import { AnimatePresence, motion } from "motion/react";
import { lazy, Suspense, useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { PageSkeleton } from "@/components/ui";
import { setUnauthorizedHandler } from "@/lib/api";
import { useMe, keys } from "@/lib/queries";
import { useQueryClient } from "@tanstack/react-query";
import { AppShell } from "./AppShell";
import { DashboardPage } from "@/features/dashboard/DashboardPage";

const TransactionsPage = lazy(() =>
  import("@/features/transactions/TransactionsPage").then((m) => ({
    default: m.TransactionsPage,
  })),
);
const AccountsPage = lazy(() =>
  import("@/features/accounts/AccountsPage").then((m) => ({ default: m.AccountsPage })),
);
const AffordPage = lazy(() =>
  import("@/features/afford/AffordPage").then((m) => ({ default: m.AffordPage })),
);
const BudgetPage = lazy(() =>
  import("@/features/budget/BudgetPage").then((m) => ({ default: m.BudgetPage })),
);
const CalendarPage = lazy(() =>
  import("@/features/calendar/CalendarPage").then((m) => ({ default: m.CalendarPage })),
);
const CardsPage = lazy(() =>
  import("@/features/cards/CardsPage").then((m) => ({ default: m.CardsPage })),
);
const ChartsPage = lazy(() =>
  import("@/features/charts/ChartsPage").then((m) => ({ default: m.ChartsPage })),
);
const DebtsPage = lazy(() =>
  import("@/features/debts/DebtsPage").then((m) => ({ default: m.DebtsPage })),
);
const ImportPage = lazy(() =>
  import("@/features/imports/ImportPage").then((m) => ({ default: m.ImportPage })),
);
const ImportReviewPage = lazy(() =>
  import("@/features/imports/ImportReviewPage").then((m) => ({ default: m.ImportReviewPage })),
);
// TEMPORARY (#31): remove with VerifyPage
const VerifyPage = lazy(() =>
  import("@/features/imports/VerifyPage").then((m) => ({ default: m.VerifyPage })),
);
const MorePage = lazy(() =>
  import("@/features/more/MorePage").then((m) => ({ default: m.MorePage })),
);
const MainAccountPage = lazy(() =>
  import("@/features/main-account/MainAccountPage").then((m) => ({ default: m.MainAccountPage })),
);
const PeriodReportPage = lazy(() =>
  import("@/features/report/PeriodReportPage").then((m) => ({ default: m.PeriodReportPage })),
);
const PlanPage = lazy(() =>
  import("@/features/plan/PlanPage").then((m) => ({ default: m.PlanPage })),
);
const PaymentsPage = lazy(() =>
  import("@/features/payments/PaymentsPage").then((m) => ({ default: m.PaymentsPage })),
);
const SavingsPage = lazy(() =>
  import("@/features/savings/SavingsPage").then((m) => ({ default: m.SavingsPage })),
);
const SettingsPage = lazy(() =>
  import("@/features/settings/SettingsPage").then((m) => ({ default: m.SettingsPage })),
);
const InvitationPage = lazy(() =>
  import("@/features/household/InvitationPage").then((m) => ({ default: m.InvitationPage })),
);
const OnboardingPage = lazy(() =>
  import("@/features/household/OnboardingPage").then((m) => ({ default: m.OnboardingPage })),
);
const PrivacyPage = lazy(() =>
  import("@/features/privacy/PrivacyPage").then((m) => ({ default: m.PrivacyPage })),
);
import { Login } from "./Login";

const fullPage = (
  <div className="mx-auto max-w-xl p-6">
    <PageSkeleton />
  </div>
);

export function App() {
  const me = useMe();
  const qc = useQueryClient();
  const location = useLocation();

  useEffect(() => {
    setUnauthorizedHandler(() => qc.setQueryData(keys.me, null));
  }, [qc]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [location.pathname]);

  if (me.isLoading) return fullPage;
  // public/standalone pages without the shell: privacy, invitation, or onboarding
  if (
    location.pathname === "/privacidad" ||
    location.pathname === "/invitacion" ||
    (me.data && location.pathname === "/bienvenida")
  ) {
    return (
      <Suspense fallback={fullPage}>
        {location.pathname === "/privacidad" ? (
          <PrivacyPage />
        ) : location.pathname === "/invitacion" ? (
          <InvitationPage user={me.data ?? null} />
        ) : (
          <OnboardingPage />
        )}
      </Suspense>
    );
  }
  if (!me.data) return <Login />;

  return (
    <AppShell>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          <Suspense fallback={fullPage}>
            <Routes location={location}>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/gastos" element={<TransactionsPage />} />
              <Route path="/tarjetas" element={<CardsPage />} />
              <Route path="/mas" element={<MorePage />} />
              <Route path="/plan" element={<PlanPage />} />
              <Route path="/cierre" element={<PeriodReportPage />} />
              <Route path="/cierre/:period" element={<PeriodReportPage />} />
              <Route path="/presupuesto" element={<BudgetPage />} />
              <Route path="/pagos" element={<PaymentsPage />} />
              <Route path="/puedo-comprar" element={<AffordPage />} />
              <Route path="/calendario" element={<CalendarPage />} />
              <Route path="/graficos" element={<ChartsPage />} />
              <Route path="/cuentas" element={<AccountsPage />} />
              <Route path="/deudas" element={<DebtsPage />} />
              <Route path="/principal" element={<MainAccountPage />} />
              <Route path="/caldero" element={<Navigate to="/principal" replace />} />
              <Route path="/ahorro" element={<SavingsPage />} />
              <Route path="/importar" element={<ImportPage />} />
              <Route path="/importar/verificar" element={<VerifyPage />} />
              <Route path="/importar/:id" element={<ImportReviewPage />} />
              <Route path="/ajustes" element={<SettingsPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </motion.div>
      </AnimatePresence>
    </AppShell>
  );
}
